import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import { X } from 'lucide-react'
import { Button } from '@/shared/ui/button'
import { Badge } from '@/shared/ui/badge'
import { Switch } from '@/shared/ui/switch'
import { Label } from '@/shared/ui/label'
import { Input } from '@/shared/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog'
import {
  RestoreDialog,
  type RestorePreviewDepartment,
  type RestoreSelection,
} from '@/shared/ui/restore-dialog'
import { t } from '@/shared/lib/i18n'
import type { ComplianceFilter } from '@/shared/lib/compliance'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip'
import { SearchableSelect } from '@/shared/ui/searchable-select'
import { ComplianceBadge } from '@/shared/ui/compliance-badge'
import {
  departmentsPath,
  deletePreviewSitePath,
  editSitePath,
  newSitePath,
  restorePreviewSitePath,
  restoreSitePath,
  sitePath,
  sitesPath,
  organizationPath,
  unitsPath,
} from '@/shared/lib/routes'
import OrganizationTabs from '../OrganizationTabs'
import { visitWithParams } from '@/shared/lib/visit-with-params'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import { useCan } from '@/shared/hooks/useCan'
import { Resources, Actions } from '@/shared/rbac'
import type { PaginationMeta } from '@/shared/types/PaginationMeta'
import type { WebSite } from '@/shared/types/web'
import type { SharedProps } from '@/shared/types/SharedProps'

interface SiteDeletePreview {
  departments_count: number
  units_count: number
  instruments_count: number
}

interface Props {
  sites: WebSite[]
  pagination: PaginationMeta
  searchQuery?: string
  filters: { showDeleted: boolean; compliance: ComplianceFilter }
  atLimit: boolean
  [key: string]: unknown
}

const SiteDepartmentsUnitsSummary = ({ site }: { site: WebSite }) => {
  const { departmentsCount, unitsCount } = site

  if (departmentsCount === 0 && unitsCount === 0) {
    return null
  }

  const hasBothCounts = departmentsCount > 0 && unitsCount > 0

  return (
    <p
      className="text-sm text-muted-foreground"
      aria-label={
        hasBothCounts
          ? t('organization.sites.summaryDepartmentsUnits', { departmentsCount, unitsCount })
          : undefined
      }
    >
      {departmentsCount > 0 && (
        <Link
          href={`${departmentsPath()}?site_id=${site.id}`}
          className="hover:text-foreground hover:underline"
        >
          {t('organization.sites.summaryDepartmentsOnly', { count: departmentsCount })}
        </Link>
      )}
      {hasBothCounts && t('organization.sites.summarySeparator')}
      {unitsCount > 0 && (
        <Link
          href={`${unitsPath()}?site_id=${site.id}`}
          className="hover:text-foreground hover:underline"
        >
          {t('organization.sites.summaryUnitsOnly', { count: unitsCount })}
        </Link>
      )}
    </p>
  )
}

const SEARCH_DEBOUNCE_MS = 300

const SitesIndexPage = () => {
  const {
    sites,
    pagination,
    searchQuery: initialSearchQuery = '',
    filters,
    atLimit,
  } = usePage<Props>().props
  const { complianceSettings } = usePage<SharedProps>().props

  // Search, Compliance filter and Show disabled all round-trip through the server and keep each
  // other's params, so they combine and apply to every page (REBUILD-534).
  const visitSites = (updates: Record<string, string | null>) =>
    visitWithParams(sitesPath(), updates)
  const toggleShowDeleted = (next: boolean) => visitSites({ show_deleted: next ? '1' : null })

  const searchInputRef = useRef<HTMLInputElement>(null)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useBreadcrumbs([
    { label: t('nav.administration'), href: organizationPath() },
    { label: t('nav.sites') },
  ])

  const [searchQuery, setSearchQuery] = useState(initialSearchQuery)
  const [deletingSite, setDeletingSite] = useState<WebSite | null>(null)
  const [deletePreview, setDeletePreview] = useState<SiteDeletePreview | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [restoringSite, setRestoringSite] = useState<WebSite | null>(null)
  const [restorePreview, setRestorePreview] = useState<RestorePreviewDepartment[]>([])
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false)
  const [restorePreviewLoading, setRestorePreviewLoading] = useState(false)

  const isAdmin = useCan(Resources.SITE, Actions.CREATE)
  const canDelete = useCan(Resources.SITE, Actions.DELETE)
  const desktopTableColumnCount = isAdmin ? 9 : 8

  const [complianceSort, setComplianceSort] = useState<'asc' | 'desc' | null>(null)
  const complianceFilter = filters.compliance ?? 'all'

  const visibleSites = useMemo(() => {
    if (!complianceSort) return sites
    return [...sites].sort((a, b) => {
      const aVal = a.rollingCompliancePercent ?? -1
      const bVal = b.rollingCompliancePercent ?? -1
      return complianceSort === 'asc' ? aVal - bVal : bVal - aVal
    })
  }, [sites, complianceSort])

  const hasActiveSearch = searchQuery.trim().length > 0
  const hasActiveFilters = hasActiveSearch || complianceFilter !== 'all'
  const activeFilterCount = [
    hasActiveSearch,
    complianceFilter !== 'all',
    filters.showDeleted,
    complianceSort !== null,
  ].filter(Boolean).length
  const isGlobalEmpty = visibleSites.length === 0 && !hasActiveFilters
  const isSearchEmpty = hasActiveFilters && visibleSites.length === 0

  const performSearch = useCallback((query: string) => {
    visitWithParams(sitesPath(), { q: query.trim() || null })
  }, [])

  const handleRestoreClick = async (site: WebSite) => {
    setRestoringSite(site)
    setRestorePreview([])
    setRestorePreviewLoading(true)
    setRestoreDialogOpen(true)
    try {
      const response = await fetch(restorePreviewSitePath(site.id), {
        headers: { Accept: 'application/json' },
      })
      if (response.ok) {
        const preview = (await response.json()) as RestorePreviewDepartment[]
        setRestorePreview(preview)
      }
    } catch {
      // preview stays empty
    } finally {
      setRestorePreviewLoading(false)
    }
  }

  const handleRestoreConfirm = (selection: RestoreSelection) => {
    if (!restoringSite) return
    router.post(restoreSitePath(restoringSite.id), {
      department_ids: selection.departmentIds,
      unit_ids: selection.unitIds,
      instrument_ids: selection.instrumentIds,
    })
    setRestoringSite(null)
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setSearchQuery(value)

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    debounceTimerRef.current = setTimeout(() => performSearch(value), SEARCH_DEBOUNCE_MS)
  }

  useEffect(
    () => () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    },
    [],
  )

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      clearSearch()
    }
    if (event.key === 'Enter') {
      event.preventDefault()
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
      performSearch(searchQuery)
    }
  }

  const clearSearch = () => {
    setSearchQuery('')
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    performSearch('')
    searchInputRef.current?.focus()
  }

  const clearFilters = () => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    setSearchQuery('')
    setComplianceSort(null)
    router.visit(sitesPath(), { preserveState: false })
  }

  const handleDeleteClick = async (site: WebSite) => {
    setDeletingSite(site)
    try {
      const response = await fetch(deletePreviewSitePath(site.id), {
        headers: { Accept: 'application/json' },
      })
      if (!response.ok) throw new Error('Preview fetch failed')
      const preview = (await response.json()) as SiteDeletePreview
      setDeletePreview(preview)
    } catch {
      setDeletePreview(null)
    }
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = () => {
    if (!deletingSite) return
    router.delete(sitePath(deletingSite.id), { preserveScroll: true })
    setDeleteDialogOpen(false)
    setDeletingSite(null)
    setDeletePreview(null)
  }

  const deleteDialogBody = () => {
    if (!deletePreview) return t('organization.sites.deleteConfirmBodyEmpty')
    const { departments_count, units_count, instruments_count } = deletePreview
    if (departments_count === 0 && units_count === 0 && instruments_count === 0) {
      return t('organization.sites.deleteConfirmBodyEmpty')
    }
    return t('organization.sites.deleteConfirmBody', {
      departmentsCount: departments_count,
      unitsCount: units_count,
      instrumentsCount: instruments_count,
    })
  }

  return (
    <div className="space-y-6">
      <OrganizationTabs />
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t('organization.sites.title')}</h1>
        {isAdmin &&
          (atLimit ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0}>
                  <Button disabled>{t('organization.sites.newSite')}</Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>{t('organization.sites.siteLimitReached')}</TooltipContent>
            </Tooltip>
          ) : (
            <Button asChild>
              <Link href={newSitePath()}>{t('organization.sites.newSite')}</Link>
            </Button>
          ))}
      </div>

      <div className="space-y-2">
        <div className="flex flex-wrap gap-3">
          <div className="relative w-full sm:flex-1 sm:max-w-sm">
            <Input
              ref={searchInputRef}
              type="text"
              placeholder={t('organization.sites.searchPlaceholder')}
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              className={hasActiveSearch ? 'pr-9' : undefined}
              autoComplete="off"
            />
            {hasActiveSearch && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-full px-2"
                aria-label={t('organization.sites.searchClearAria')}
                onClick={clearSearch}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          <SearchableSelect
            className="w-52"
            value={complianceFilter}
            onValueChange={(value) => visitSites({ compliance: value === 'all' ? null : value })}
            placeholder={t('organization.sites.filterByCompliance')}
            options={[
              { value: 'all', label: t('organization.sites.allCompliance') },
              { value: 'high', label: t('organization.sites.complianceHigh') },
              { value: 'medium', label: t('organization.sites.complianceMedium') },
              { value: 'low', label: t('organization.sites.complianceLow') },
              { value: 'none', label: t('organization.sites.complianceNone') },
            ]}
          />
          <div className="ml-auto flex items-center gap-2 shrink-0">
            <Switch
              id="sites-show-deleted"
              checked={filters.showDeleted}
              onCheckedChange={toggleShowDeleted}
            />
            <Label htmlFor="sites-show-deleted">{t('organization.sites.showDeleted')}</Label>
            {activeFilterCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="ml-2 text-muted-foreground"
                onClick={clearFilters}
              >
                {t('organization.sites.clearFilters')}
              </Button>
            )}
          </div>
        </div>

        {hasActiveSearch && (
          <p className="text-sm text-muted-foreground">
            {visibleSites.length > 0
              ? t('organization.sites.searchResultsCount', { count: pagination.total })
              : t('organization.sites.searchNoResults')}
          </p>
        )}
      </div>

      {/* Mobile: card list */}
      <div className="space-y-3 lg:hidden">
        {isGlobalEmpty ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <p className="text-sm text-muted-foreground">{t('organization.sites.empty')}</p>
          </div>
        ) : isSearchEmpty ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <p className="text-sm text-muted-foreground">
              {t('organization.sites.searchNoResults')}
            </p>
          </div>
        ) : (
          visibleSites.map((site) => (
            <div
              key={site.id}
              className={`space-y-3 rounded-lg border bg-card px-4 py-4 shadow-sm${site.discarded ? ' opacity-60' : ''}`}
            >
              <div className="min-h-11 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{site.name}</span>
                  {site.discarded && (
                    <Badge variant="secondary">{t('organization.sites.deleted')}</Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('organization.sites.table.id')}: {site.id}
                </p>
                {!site.discarded && <SiteDepartmentsUnitsSummary site={site} />}
              </div>
              {!site.discarded && (
                <p className="text-sm text-muted-foreground">
                  {[site.address, site.city, site.state].filter(Boolean).join(', ') || '—'}
                </p>
              )}
              {(site.timezoneName || site.countryName) && (
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  {site.timezoneName && <span>{site.timezoneName}</span>}
                  {site.countryName && <span>{site.countryName}</span>}
                </div>
              )}
              <div className="text-xs">
                <ComplianceBadge
                  percent={site.rollingCompliancePercent}
                  windowDays={complianceSettings?.windowDays}
                  colorCodes={complianceSettings?.colorCodes}
                />
              </div>
              {isAdmin && (
                <div className="flex justify-end gap-2 pt-1">
                  {site.discarded ? (
                    canDelete && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => void handleRestoreClick(site)}
                      >
                        {t('organization.sites.restore')}
                      </Button>
                    )
                  ) : (
                    <>
                      <Button asChild variant="outline" size="sm">
                        <Link href={editSitePath(site.id)}>{t('organization.sites.edit')}</Link>
                      </Button>
                      {canDelete && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => void handleDeleteClick(site)}
                        >
                          {t('organization.sites.delete')}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Desktop: table */}
      <div className="hidden rounded-md border lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">{t('organization.sites.table.id')}</TableHead>
              <TableHead>{t('organization.sites.table.name')}</TableHead>
              <TableHead>{t('organization.sites.table.address')}</TableHead>
              <TableHead>{t('organization.sites.table.timezone')}</TableHead>
              <TableHead>{t('organization.sites.table.country')}</TableHead>
              <TableHead>{t('organization.sites.table.departments')}</TableHead>
              <TableHead>{t('organization.sites.table.units')}</TableHead>
              <TableHead
                className="cursor-pointer select-none whitespace-nowrap"
                onClick={() =>
                  setComplianceSort((prev) =>
                    prev === 'asc' ? 'desc' : prev === 'desc' ? null : 'asc',
                  )
                }
              >
                {t('organization.sites.table.complianceScore')}
                {complianceSort === 'asc' ? ' ↑' : complianceSort === 'desc' ? ' ↓' : ' ↕'}
              </TableHead>
              {isAdmin && (
                <TableHead className="w-[1%] text-right">
                  {t('organization.sites.table.actions')}
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isGlobalEmpty ? (
              <TableRow>
                <TableCell
                  colSpan={desktopTableColumnCount}
                  className="h-24 text-center text-muted-foreground"
                >
                  {t('organization.sites.empty')}
                </TableCell>
              </TableRow>
            ) : isSearchEmpty ? (
              <TableRow>
                <TableCell
                  colSpan={desktopTableColumnCount}
                  className="h-24 text-center text-muted-foreground"
                >
                  {t('organization.sites.searchNoResults')}
                </TableCell>
              </TableRow>
            ) : (
              visibleSites.map((site) => (
                <TableRow key={site.id} className={site.discarded ? 'opacity-60' : undefined}>
                  <TableCell className="text-xs text-muted-foreground">{site.id}</TableCell>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      {site.name}
                      {site.discarded && (
                        <Badge variant="secondary">{t('organization.sites.deleted')}</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {[site.address, site.city, site.state].filter(Boolean).join(', ')}
                  </TableCell>
                  <TableCell>{site.timezoneName ?? '—'}</TableCell>
                  <TableCell>{site.countryName ?? '—'}</TableCell>
                  <TableCell>
                    {!site.discarded && site.departmentsCount > 0 ? (
                      <Link
                        href={`${departmentsPath()}?site_id=${site.id}`}
                        className="text-primary hover:underline"
                      >
                        {site.departmentsCount}
                      </Link>
                    ) : (
                      site.departmentsCount
                    )}
                  </TableCell>
                  <TableCell>
                    {!site.discarded && site.unitsCount > 0 ? (
                      <Link
                        href={`${unitsPath()}?site_id=${site.id}`}
                        className="text-primary hover:underline"
                      >
                        {site.unitsCount}
                      </Link>
                    ) : (
                      site.unitsCount
                    )}
                  </TableCell>
                  <TableCell>
                    <ComplianceBadge
                      percent={site.rollingCompliancePercent}
                      windowDays={complianceSettings?.windowDays}
                      colorCodes={complianceSettings?.colorCodes}
                    />
                  </TableCell>
                  {isAdmin && (
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {site.discarded ? (
                          canDelete && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => void handleRestoreClick(site)}
                            >
                              {t('organization.sites.restore')}
                            </Button>
                          )
                        ) : (
                          <>
                            <Button asChild variant="outline" size="sm">
                              <Link href={editSitePath(site.id)}>
                                {t('organization.sites.edit')}
                              </Link>
                            </Button>
                            {canDelete && (
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => void handleDeleteClick(site)}
                              >
                                {t('organization.sites.delete')}
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('organization.sites.deleteConfirmTitle')}: {deletingSite?.name}
            </AlertDialogTitle>
            <AlertDialogDescription>{deleteDialogBody()}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('organization.sites.form.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('organization.sites.deleteConfirmAction')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <RestoreDialog
        open={restoreDialogOpen}
        onOpenChange={setRestoreDialogOpen}
        resourceName={restoringSite?.name ?? ''}
        loading={restorePreviewLoading}
        departments={restorePreview}
        onConfirm={handleRestoreConfirm}
      />

      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>
          {pagination.totalPages > 1 &&
            `${t('organization.sites.paginationPage')} ${pagination.page} ${t('organization.sites.paginationOf')} ${pagination.totalPages} (`}
          {pagination.total} {t('organization.sites.paginationNoun')}
          {pagination.totalPages > 1 && ')'}
        </span>
        <div className="flex gap-2">
          {pagination.page > 1 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const params = new URLSearchParams(window.location.search)
                params.set('page', String(pagination.page - 1))
                router.visit(`${sitesPath()}?${params}`, { preserveState: true })
              }}
            >
              {t('organization.sites.previous')}
            </Button>
          )}
          {pagination.page < pagination.totalPages && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const params = new URLSearchParams(window.location.search)
                params.set('page', String(pagination.page + 1))
                router.visit(`${sitesPath()}?${params}`, { preserveState: true })
              }}
            >
              {t('organization.sites.next')}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

export default SitesIndexPage
